// =============================================================
// damApi.js — client helpers for the Digital Asset Management module.
//
// Part of the removable DAM module (frontend/src/pages/dam/*). Thin wrappers
// over the shared `api` client plus a few pure display helpers. Delete the
// whole folder to remove the frontend (see DAM_MODULE.md).
// =============================================================
import { api } from '../../api/client';
import { getAccessToken } from '../../config/auth';
import { resumableUpload } from './blockUpload';

// File-type categories — must mirror TYPE_CATEGORIES in api/src/functions/dam.js.
export const TYPE_CATEGORIES = {
  image:    ['png', 'jpg', 'jpeg', 'webp', 'gif', 'avif', 'tif', 'tiff', 'raw', 'heic', 'bmp', 'svg'],
  video:    ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v'],
  audio:    ['mp3', 'wav', 'aac', 'm4a', 'flac', 'ogg'],
  document: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'],
  design:   ['psd', 'ai', 'aep', 'cad', 'eps', 'sketch', 'fig', 'indd'],
};

// Raw vs. Edited — ONE list of labels for every surface that offers the choice
// (the upload modal, the asset detail view, the triage queue). Three copies of a
// four-item select is how one of them ends up saying "Edited" where another says
// "Finished" about the same stored value.
//
// The values match api/src/damIntake.js: 'raw' | 'edited' | 'unknown', with ''
// meaning "nobody has said" — which is NOT the same as 'unknown' ("somebody
// looked and could not tell"), and is what leaves the AI tagger free to answer.
export const RAW_EDITED_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'raw', label: 'Raw (unedited original)' },
  { value: 'edited', label: 'Edited / finished' },
  { value: 'unknown', label: 'Unsure' },
];

// The same list for the upload modal, where '' has a more useful name: the point
// there is that leaving it alone hands the question to Claude, and a box reading
// "Not specified" does not say so.
export const RAW_EDITED_UPLOAD_OPTIONS = [
  { value: '', label: 'Let Claude decide' },
  ...RAW_EDITED_OPTIONS.slice(1, 3),
];

export function categoryOf(fileType) {
  const ext = (fileType || '').toLowerCase();
  for (const [cat, exts] of Object.entries(TYPE_CATEGORIES)) {
    if (exts.includes(ext)) return cat;
  }
  return 'other';
}

// Semantic Icon name for a file category (see components/ui/Icon.jsx).
export function iconForCategory(cat) {
  return { image: 'image', video: 'play', audio: 'note', document: 'file', design: 'layers' }[cat] || 'file';
}

export function isImageType(fileType, mimeType) {
  if (mimeType && mimeType.startsWith('image/')) return true;
  return categoryOf(fileType) === 'image';
}

export function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return '';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

// Longest-edge cap for the AI-tagging image. Claude's vision API resizes large
// images down anyway, and its ~5MB base64 limit means large originals (e.g. a
// full-res phone photo) can't be sent as-is — so we downscale in the browser.
const TAG_IMAGE_MAX_EDGE = 1568;

// Longest-edge cap for the stored grid thumbnail. Small enough that the library
// grid loads fast instead of fetching full-res originals, big enough to stay
// crisp on hi-DPI cards (grid tiles are ~180-260px wide).
const THUMB_MAX_EDGE = 512;

// Draw `img` into a downscaled JPEG (longest edge capped at `maxEdge`) and return
// its base64 payload, or '' on any canvas error.
function downscaleToJpegBase64(img, maxEdge, quality) {
  try {
    const longest = Math.max(img.naturalWidth, img.naturalHeight) || 1;
    const scale = Math.min(1, maxEdge / longest);
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(img, 0, 0, w, h);
    return (canvas.toDataURL('image/jpeg', quality).split(',')[1]) || '';
  } catch {
    return ''; // tainted/unsupported canvas
  }
}

// Load an image file once and (best-effort) return its pixel dimensions, a
// downscaled JPEG for server-side AI tagging, and a smaller thumbnail JPEG for
// the grid. Resolves { dims:{}, tag:null, thumb:null } for non-images or on
// error. The tag image lets the API auto-tag originals that are over Claude's
// size cap, or in formats (HEIC, TIFF…) the browser can render to a canvas but
// the vision API can't accept directly; the thumb means the grid never has to
// download the full-res original.
function loadImageForUpload(file) {
  return new Promise((resolve) => {
    if (!file.type?.startsWith('image/')) return resolve({ dims: {}, tag: null, thumb: null });
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      const tagBase64 = downscaleToJpegBase64(img, TAG_IMAGE_MAX_EDGE, 0.85);
      const thumbBase64 = downscaleToJpegBase64(img, THUMB_MAX_EDGE, 0.8);
      URL.revokeObjectURL(url);
      resolve({
        dims,
        tag: tagBase64 ? { base64: tagBase64, mimeType: 'image/jpeg' } : null,
        thumb: thumbBase64 ? { base64: thumbBase64, mimeType: 'image/jpeg' } : null,
      });
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve({ dims: {}, tag: null, thumb: null }); };
    img.src = url;
  });
}

// Full upload flow: get a SAS write URL → stage the file to Blob Storage in
// resumable blocks (survives network drops / reloads, reports byte progress) →
// confirm (which triggers server-side AI auto-tagging). Returns { assetId, ai }.
// `onProgress({ loaded, total })` fires during the block staging.
export async function uploadAsset(file, { collectionId, rawEdited, onProgress } = {}) {
  const { dims, tag, thumb } = await loadImageForUpload(file);
  const fileType = (file.name.split('.').pop() || '').toLowerCase();

  const { assetId } = await resumableUpload(file, {
    getUploadUrl: (extra) => api.post('dam/upload-url', {
      fileName: file.name,
      fileType,
      mimeType: file.type || null,
      fileSizeBytes: file.size,
      title: file.name,
      collectionId: collectionId || null,
      // Raw vs. Edited as DECLARED BY THE UPLOADER. Sent with the upload-url
      // call, not the confirm, because that is the write the server's
      // `COALESCE(RawEdited, …)` protects — arriving later it would be racing
      // the AI guess it is supposed to outrank. Empty means "not stated", which
      // leaves the guess to answer.
      rawEdited: rawEdited || null,
      // The folder this file came in with, when it was added from a directory
      // rather than picked one by one. Populated by the browser; '' otherwise.
      relativePath: file.webkitRelativePath || null,
      ...extra,
    }),
    onProgress,
  });

  // Confirm runs synchronous Claude vision auto-tagging server-side, which
  // regularly takes longer than the client's default 10s budget. The blob PUT
  // above has already succeeded by this point, so a short timeout would abort
  // the confirm and surface a scary "timed out" error even though the asset
  // finishes uploading + tagging server-side. Give it a generous budget
  // (mirrors the Harvi chat, the other Claude-backed call path).
  const res = await api.post(`dam/assets/${assetId}/confirm`, {
    width: dims.width ?? null,
    height: dims.height ?? null,
    fileSizeBytes: file.size,
    mimeType: file.type || null,
    // Downscaled JPEG for server-side AI tagging (best-effort; null for non-images).
    tagImageBase64: tag?.base64 || null,
    tagImageMimeType: tag?.mimeType || null,
    // Small thumbnail JPEG stored for the grid (best-effort; null for non-images).
    thumbBase64: thumb?.base64 || null,
    thumbMimeType: thumb?.mimeType || null,
  }, { timeout: 120000 });
  return { assetId, ai: res?.ai || null };
}

// Trigger a browser download for one asset.
//
// Without size options → the original, fetched via a fresh read SAS URL and
// downloaded directly from Blob Storage (fast, no proxying).
//
// With size options ({ width, height, maxMB, format }) → an authenticated
// request to the resize endpoint, which returns a resized image buffer from our
// own origin (so no cross-origin/CORS issue). Non-images ignore the size
// options server-side and fall back to the original.
export async function downloadAsset(assetId, opts = {}) {
  const { width, height, maxMB, format } = opts;
  const wantsResize = width || height || maxMB;

  if (wantsResize) {
    const params = new URLSearchParams();
    if (width)  params.set('width', width);
    if (height) params.set('height', height);
    if (maxMB)  params.set('maxMB', maxMB);
    if (format) params.set('format', format);
    const token = await getAccessToken();
    const res = await fetch(`/api/dam/assets/${assetId}/download?${params.toString()}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const fileName = m ? m[1] : 'download';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return;
  }

  const { url, fileName } = await api.get(`dam/assets/${assetId}/download-url`);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// =============================================================
// Claude MCP connectors — expose a collection to a claude.ai Project as a
// Custom Connector. Admin-only endpoints (api/src/functions/damMcp.js).
// =============================================================

// Mint a connector for a collection. Returns { connectorId, token, mcpUrl, mcpPath }.
export function createConnector(body) {
  return api.post('dam/connectors', body);
}

// List connectors for a collection (with live status + call counts + mcpUrl).
export function listConnectors(collectionId) {
  const qs = collectionId ? `?collectionId=${encodeURIComponent(collectionId)}` : '';
  return api.get(`dam/connectors${qs}`);
}

// The per-connector call log.
export function connectorEvents(connectorId) {
  return api.get(`dam/connectors/${connectorId}/events`);
}

// Turn a connector off (claude.ai loses access immediately).
export function revokeConnector(connectorId) {
  return api.post(`dam/connectors/${connectorId}/revoke`, {});
}

// Soft-delete a connector and its call log.
export function deleteConnector(connectorId) {
  return api.del(`dam/connectors/${connectorId}`);
}

// =============================================================
// Intake triage — assets an ingest brought in that nobody has filed yet.
// They are held OUT of the library (they are not "uncollected") until somebody
// answers the question these three calls exist to ask.
// =============================================================

// The queue, grouped by the folder each drop arrived in.
export function fetchIntake() {
  return api.get('dam/intake', { timeout: 30000 });
}

// File a batch into a collection. Pass `collectionId` for one that exists, or
// `newCollectionName` to create it in the same call — two calls would leave a
// stray collection behind whenever the second half failed.
export function fileIntake({ assetIds, collectionId, newCollectionName }) {
  return api.post('dam/intake/file', {
    assetIds,
    collectionId: collectionId || null,
    newCollectionName: newCollectionName || null,
  }, { timeout: 60000 });
}

// "This should not be here at all" — a sync artefact, a duplicate drop. Without
// it the only way to clear such a row would be to file it into a collection
// first, putting junk in the library on the way to removing it.
export function discardIntake(assetIds) {
  return api.post('dam/intake/discard', { assetIds }, { timeout: 60000 });
}

// ── Whole-library reset (Admin) ─────────────────────────────────────────────
// Two calls, deliberately: the GET sizes the job so the confirmation can name
// what it is about to destroy, and the POST does one bounded lap. The POST is
// repeated by the caller while `done` is false — the server cannot finish
// thousands of blob deletes inside one request without meeting the ~45s edge
// timeout, and a deleted blob stops being listed, so re-posting IS the resume.
// Both calls enumerate a container server-side, so the client's 10s default
// would report a timeout over a lap that is deleting files perfectly well —
// the SageImport.jsx lesson.
const RESET_TIMEOUT_MS = 120_000;

export function fetchResetPreview() {
  return api.get('dam/admin/reset', { timeout: RESET_TIMEOUT_MS });
}

// The confirmation phrase is NOT hardcoded here — it comes from the preview,
// so the words the operator must type can never drift from the words the API
// compares against. A refusal is surfaced verbatim by the caller.
export function runResetLap(confirm) {
  return api.post('dam/admin/reset', { confirm }, { timeout: RESET_TIMEOUT_MS });
}

